import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const safeName = (s) => String(s || "").replace(/[<>:"/\\|?*]+/g, "").trim();

export async function exportCarIdleToExcel(rows, summary, meta) {
  if (!rows || rows.length === 0) return;

  const { month, companyLabel, generatedAt } = meta || {};
  const wb = new ExcelJS.Workbook();
  wb.creator = "CLL Dashboard";
  wb.created = new Date();

  const ws = wb.addWorksheet("Car Idle Report", {
    properties: { defaultRowHeight: 18 },
    views: [{ state: "frozen", ySplit: 6 }],
  });

  // Title
  ws.mergeCells("A1:J1");
  ws.getCell("A1").value = "Car Idle Report (Unassigned + Manual)";
  ws.getCell("A1").font = { bold: true, size: 16 };
  ws.getCell("A1").alignment = { vertical: "middle", horizontal: "left" };

  ws.mergeCells("A2:J2");
  ws.getCell("A2").value = `Month: ${month || "-"}   |   Company: ${companyLabel || "All Companies"}`;
  ws.getCell("A2").font = { bold: true, size: 11 };

  ws.mergeCells("A3:J3");
  ws.getCell("A3").value = `Generated: ${generatedAt ? new Date(generatedAt).toLocaleString() : new Date().toLocaleString()}`;
  ws.getCell("A3").font = { size: 10, color: { argb: "FF666666" } };

  // Summary (clear labels)
  ws.mergeCells("A4:J4");
  ws.getCell("A4").value =
    `Idle Cars: ${summary?.idleCars ?? rows.length}   |   ` +
    `Unassigned Idle Cars: ${summary?.unassignedIdleCars ?? "-"}   |   ` +
    `Manual Idle Cars: ${summary?.manualIdleCars ?? "-"}   |   ` +
    `Unassigned Idle Days (sum): ${summary?.unassignedIdleDaysTotal ?? "-"}   |   ` +
    `Manual Idle Days (sum): ${summary?.manualIdleDaysTotal ?? "-"}   |   ` +
    `Total Idle Days (sum): ${summary?.totalIdleDaysTotal ?? "-"}`;
  ws.getCell("A4").font = { size: 10 };
  ws.getCell("A4").alignment = { wrapText: true };

  ws.addRow([]); // row 5 spacer

  // Header row (row 6)
  const header = ws.addRow([
    "Car",
    "Company",
    "Model",
    "Total Days",
    "Assigned Days",
    "Unassigned Idle",
    "Manual Idle",
    "Total Idle",
    "Utilization %",
    "Idle Details",
  ]);

  header.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E4DB7" } };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  ws.autoFilter = "A6:J6";

  ws.columns = [
    { width: 18 }, // Car
    { width: 34 }, // Company
    { width: 20 }, // Model
    { width: 11 }, // Total Days
    { width: 13 }, // Assigned Days
    { width: 14 }, // Unassigned
    { width: 12 }, // Manual
    { width: 12 }, // Total
    { width: 13 }, // Util%
    { width: 70 }, // Details
  ];

  // Rows
  rows.forEach((r) => {
    const utilPct = typeof r.utilization === "number" ? r.utilization : 0;

    const row = ws.addRow([
      r.carNumber || "-",
      r.company || "-",
      r.vehicleType || "-",
      Number(r.totalDays || 0),
      Number(r.assignedDays || 0),
      Number(r.unassignedIdleDays || 0),
      Number(r.manualIdleDays || 0),
      Number(r.totalIdleDays || 0),
      utilPct, // percent
      r.idleDetailsText || "-",
    ]);

    row.eachCell((cell, col) => {
      cell.alignment = {
        vertical: "middle",
        horizontal: col >= 4 && col <= 9 ? "center" : "left",
        wrapText: col === 10,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FFBFBFBF" } },
        left: { style: "thin", color: { argb: "FFBFBFBF" } },
        bottom: { style: "thin", color: { argb: "FFBFBFBF" } },
        right: { style: "thin", color: { argb: "FFBFBFBF" } },
      };
    });

    // Utilization percent format
    row.getCell(9).numFmt = "0%";

    // Heat highlight by Total Idle
    const totalIdle = Number(r.totalIdleDays || 0);
    if (totalIdle >= 15) {
      row.eachCell((c) => (c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFE5E5" } }));
      row.getCell(8).font = { bold: true, color: { argb: "FFB00020" } };
    } else if (totalIdle >= 7) {
      row.eachCell((c) => (c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF4E5" } }));
      row.getCell(8).font = { bold: true, color: { argb: "FF8A4B00" } };
    }
  });

  const fileName = safeName(`Car Idle Report_${month || ""}_${companyLabel || "All Companies"}.xlsx`);
  const buf = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    fileName
  );
}